import clsx from 'clsx'
import { useEffect, useMemo, useState } from 'react'
import { EmptyState, KpiCard, KpiStrip } from '../../../cc'
import { ReportTable } from '../../../cc/ReportTable'
import type { ReportColumnDef } from '../../../cc/ReportTable.types'
import { TONE_BG, TONE_TEXT, type Tone } from '../../../cc/tokens'
import { useFleet } from '../../../state/store'
import type { Triage } from '../../../lib/types'
import { exportXlsx } from '../../useReportData'
import {
  reportCard, reportCardPadSm, reportCardSub, reportCardTitle,
  reportLoading, reportSectionSub, reportSectionTitle,
} from '../../ui'
import { isNarrowed, toIstNaive, windowLabel, type AlertFilters } from '../alertFilters'
import { kindLabel } from '../alertLabels'
import { AlertDrillList, type DrillScope } from './AlertDrillList'
import { useViewExport } from './viewKit'

/**
 * The original Reports > Alerts page ("what is firing", top offenders, the drowning
 * banner), now the Overview sub-tab of the alerts report. It no longer owns a filter bar:
 * the shared AlertFilters state comes in as a prop and is mapped onto the OLD filtered
 * routes' parameters (`from/to/kind/driver_name/vehicle_id`) -- those routes take one
 * kind, so only the first selected kind applies, and they know nothing of severity, hub
 * or shift. The other sub-tabs use the new site-aware analytics endpoints; this one is
 * kept as-is because its aggregates (alert_profile / noise / repeat_offenders) are the
 * numbers ops already read every morning.
 *
 * The old routes are Ultratech-gated server-side (require_site('ultratech') in app.py's
 * middleware), so any other site shows an explicit empty state instead of a 403.
 */

interface Row { [k: string]: unknown }

const num = (v: unknown): number | null =>
  v === null || v === undefined || v === '' ? null : Number(v)

// The 4 aggregates below are re-computed server-side per filter (see
// f_alert_profile's own comment for why a filter has to run inside the
// aggregation, not hide rows client-side after it) -- these hit the dedicated
// filtered routes, not the generic /api/reports/{name} passthrough.
async function getFilteredReport(path: string, params: URLSearchParams): Promise<Row[]> {
  const qs = params.toString()
  const r = await fetch(`/api/reports/${path}${qs ? `?${qs}` : ''}`)
  if (!r.ok) throw new Error(`${path}: ${r.status}`)
  const j = (await r.json()) as { rows?: Row[] }
  return j.rows ?? []
}

function toneFor(share: number): Tone {
  if (share > 50) return 'danger'
  if (share > 25) return 'warn'
  return 'neutral'
}

// Same vocabulary as CommandCentre.tsx's CARD_EXCEPTION_KINDS -- the kinds that actually
// tint a fleet-card exception chip and drive the map's per-vehicle flag, as opposed to
// everything else on the alert tape that reaches a report but never a dispatcher's queue.
const ACTIONABLE_KINDS = new Set([
  'LOW_SOC', 'OVERSPEED', 'GHAT_OVERSPEED', 'HARSH_ACCEL', 'HARSH_BRAKE',
  'UNSCHEDULED_STOP', 'TAT_BREACH',
])

const profileColumns = ['kind', 'alerts', 'share_pct', 'vehicles', 'worst_one_subject', 'critical']
// Human headings for the Excel export (the table's own headers come from columnMeta).
const profileLabels: Record<string, string> = {
  kind: 'Alert type', alerts: 'Alerts', share_pct: 'Share of alerts (%)', vehicles: 'Vehicles',
  worst_one_subject: 'Worst vehicle', critical: 'Critical',
}
const offenderColumns = [
  'driver_name', 'vehicle_id', 'vehicles_driven', 'km', 'legs', 'events_per_100km',
  'overspeed_per_100km', 'harsh_brake_per_100km', 'escalations',
]
const offenderLabels: Record<string, string> = {
  driver_name: 'Driver', vehicle_id: 'Vehicle', vehicles_driven: 'Vehicles driven', km: 'Km', legs: 'Legs',
  events_per_100km: 'Events per 100 km', overspeed_per_100km: 'Overspeed per 100 km',
  harsh_brake_per_100km: 'Harsh brakes per 100 km', escalations: 'Escalations',
}

const btnGhost = 'rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-500 hover:border-slate-300 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]'

/** A stable, order-independent fingerprint of the triage map's content -- equal for two
 *  Maps holding the same entries, different the moment any alert's state changes. */
export function triageSignature(triage: ReadonlyMap<string, Triage>): string {
  const parts: string[] = []
  for (const [id, t] of triage) parts.push(`${id}:${t.state}:${t.ts ?? ''}`)
  return parts.sort().join('|')
}

export function OverviewView({ filters, setFilters }: {
  filters: AlertFilters
  /** Only the alert drill-down at the bottom uses it (opening the trip drawer). */
  setFilters?: (patch: Partial<AlertFilters>) => void
}) {
  // A kind row or an offender card narrows the alert list at the bottom of the page.
  const [pick, setPick] = useState<{ by: 'kind' | 'driver'; value: string } | null>(null)
  const togglePick = (by: 'kind' | 'driver', value: string) =>
    setPick((cur) => (cur && cur.by === by && cur.value === value ? null : { by, value }))
  const scope: DrillScope | null = useMemo(() => (pick ? {
    label: pick.by === 'kind' ? kindLabel(pick.value) : pick.value,
    params: { [pick.by]: pick.value },
    onClear: () => setPick(null),
  } : null), [pick])
  const [profile, setProfile] = useState<Row[]>([])
  const [noise, setNoise] = useState<Row[]>([])
  const [offenders, setOffenders] = useState<Row[]>([])
  const [actionableOnly, setActionableOnly] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  // Re-fetch on a real ack/resolve/reopen anywhere in the app -- these aggregates are
  // server-computed from `alert.acknowledged_at`. Keyed on the triage CONTENT, not the Map:
  // the 8s /api/state heal-poll and every 'triage_sync' rebuild the Map from scratch even
  // when nothing changed, and depending on its identity re-ran all three report queries
  // (and flashed the loading state) every ~8 seconds. A string selector compares by value,
  // so an identical rebuilt Map no longer re-renders this view at all.
  const triage = useFleet((s) => triageSignature(s.triage))

  const isUltratech = filters.site === 'ultratech'
  // The old routes take one kind and naive IST timestamps (datetime-local shaped, no
  // offset) -- exactly what the pre-rebuild filter bar used to send.
  const kind = filters.kind[0] ?? ''
  const { from, to, driver, vehicle } = filters

  useEffect(() => {
    if (!isUltratech) return
    let alive = true
    setLoading(true)
    setErr(null)
    const params = new URLSearchParams()
    params.set('from', toIstNaive(from))
    params.set('to', toIstNaive(to))
    if (kind) params.set('kind', kind)
    if (driver && driver !== 'unassigned') params.set('driver_name', driver)
    if (vehicle) params.set('vehicle_id', vehicle)
    const noiseParams = new URLSearchParams(params)
    noiseParams.delete('kind') // kind is noise's own group-by axis, not a filter on it
    Promise.all([
      getFilteredReport('alert-profile', params),
      getFilteredReport('alert-noise', noiseParams),
      getFilteredReport('alert-repeat-offenders', params),
    ])
      .then(([p, n, o]) => {
        if (!alive) return
        setProfile(p); setNoise(n); setOffenders(o)
      })
      .catch((e: unknown) => { if (alive) setErr(String(e)) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [isUltratech, from, to, kind, driver, vehicle, triage])

  const total = useMemo(() => profile.reduce((s, r) => s + (num(r.alerts) ?? 0), 0), [profile])
  const critical = useMemo(() => profile.reduce((s, r) => s + (num(r.critical) ?? 0), 0), [profile])
  const acked = useMemo(() => profile.reduce((s, r) => s + (num(r.acknowledged) ?? 0), 0), [profile])
  // Memoized: an unstable array reference here (a fresh .filter() result every render)
  // makes ReportTable -- which remounts its <tbody> and replays the row-entrance
  // animation whenever the `rows` reference changes -- treat every re-render as a new
  // dataset, so the filtered rows blink continuously instead of animating in once.
  const visibleProfile = useMemo(
    () => actionableOnly ? profile.filter((r) => ACTIONABLE_KINDS.has(String(r.kind))) : profile,
    [profile, actionableOnly],
  )
  const drowning = noise.filter((r) => (num(r.share_pct) ?? 0) > 50)

  const [exportError, setExportError] = useState<string | null>(null)
  const subtitle = windowLabel(filters)
  const downloadXlsx = (rows: Row[], columns: string[], filename: string, title: string, labels: Record<string, string>) => {
    setExportError(null)
    return exportXlsx(rows, columns, filename, { title, subtitle, labels }).catch((e: unknown) =>
      setExportError(e instanceof Error ? e.message : 'Excel export failed'))
  }
  // "Export this view" in the shared filter bar exports the profile table -- the
  // offenders table keeps its own button below, as before.
  const exportProfile = useMemo(
    () => (visibleProfile.length
      ? () => downloadXlsx(visibleProfile, profileColumns, 'alert-profile', 'Alert profile', profileLabels)
      : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleProfile, subtitle],
  )
  useViewExport(exportProfile)

  const profileDefs: Record<string, Partial<ReportColumnDef>> = {
    // getColumnMeta infers a column's header alignment from its NAME alone (no row value
    // to look at yet) but a data cell's alignment from the actual value's type -- "vehicles"
    // isn't a _count/_id/_km column by name, so the header fell back to left while every
    // numeric cell beneath it inferred right. Pinning it explicitly keeps header and cells
    // in the same column.
    vehicles: { align: 'right' },
    share_pct: {
      align: 'right',
      render: (v) => {
        const share = num(v) ?? 0
        const tone = toneFor(share)
        const barColor = tone === 'danger' ? 'bg-red-500' : tone === 'warn' ? 'bg-amber-500' : 'bg-slate-400'
        return (
          // justify-end: the header above is right-aligned (format: 'percent'), but a flex
          // row's own text-align from its <td> parent does nothing for its children's
          // layout -- without this the bar+figure sat flush left under a right-aligned
          // header.
          <div className="flex items-center justify-end gap-2">
            <div className="h-1.5 w-24 rounded-full bg-slate-100">
              <div className={`h-1.5 rounded-full ${barColor}`} style={{ width: `${Math.max(2, Math.min(100, share))}%` }} />
            </div>
            <span className={clsx('text-xs font-mono tabular-nums', TONE_TEXT[tone])}>{share}%</span>
          </div>
        )
      },
    },
    worst_one_subject: {
      render: (v, row) => {
        const worst = num(v) ?? 0
        const alerts = num(row.alerts) ?? 0
        const vehicles = num(row.vehicles) ?? 0
        const concentrated = vehicles > 0 && worst / Math.max(alerts, 1) > 0.6
        return (
          <span className={clsx('font-mono tabular-nums', concentrated ? clsx('font-medium', TONE_TEXT.warn) : 'text-slate-500')}>
            {worst}
            {concentrated ? <div className={clsx('text-xs', TONE_TEXT.warn)}>one vehicle</div> : null}
          </span>
        )
      },
    },
    critical: {
      render: (v) => {
        const c = num(v) ?? 0
        return (
          <span className={clsx('font-mono tabular-nums', c > 0 ? clsx('font-medium', TONE_TEXT.danger) : 'text-slate-400')}>
            {String(v)}
          </span>
        )
      },
    },
  }

  if (!isUltratech) {
    return (
      <div className={`${reportCard} ${reportCardPadSm}`}>
        <EmptyState
          title="Overview is available for Ultratech only"
          body="Its alert-profile and repeat-offender aggregates are Ultratech routes. The Drivers, Vehicles, Locations, Day/Night, Trends and Alert list tabs cover every site."
        />
      </div>
    )
  }

  if (loading) return <div className={reportLoading} aria-busy="true" aria-live="polite">Reading the alert record…</div>

  if (err) {
    return (
      <div className={`${reportCard} ${reportCardPadSm}`} role="alert">
        <EmptyState title="The reporting database is not answering" body={err} />
      </div>
    )
  }

  // Severity/hub/shift are not applied by these routes, but a kind/driver/vehicle filter
  // is -- so "filtered to nothing" is decided on what actually reached the server.
  const filtersActive = Boolean(kind || driver || vehicle || actionableOnly) || isNarrowed(filters)

  if (!profile.length) {
    return (
      <div className={`${reportCard} ${reportCardPadSm}`}>
        <EmptyState
          title={filtersActive ? 'No alerts match this filter' : 'No alerts recorded'}
          body={filtersActive
            ? 'Widen the date range, or clear the type/driver/vehicle filter above.'
            : 'Quiet is only good news if the pipeline is running. Check the stages are healthy before reading this as a clean fleet.'}
        />
      </div>
    )
  }

  const worstRate = offenders[0] ? num(offenders[0].events_per_100km) ?? 0 : 0

  return (
    <div className="space-y-6 pb-4">
      {(filters.severity.length || filters.hub || filters.shift) ? (
        <p className="text-[11.5px] text-slate-500">
          Overview applies the window, alert type, driver and vehicle filters only -- severity, place and shift are not applied here.
        </p>
      ) : null}
      {exportError ? <p role="alert" className="text-[11px] font-medium text-red-600">{exportError}</p> : null}

      {drowning.length > 0 && (
        <div className={clsx('rounded-xl border p-4 shadow-sm ring-1', TONE_BG.danger, 'border-red-200/80 ring-red-900/5')}>
          <div className={clsx('text-sm font-semibold', TONE_TEXT.danger)}>
            {String(drowning[0]?.kind)} is {String(drowning[0]?.share_pct)}% of every alert
          </div>
          <p className={clsx('mt-1 max-w-prose text-xs', TONE_TEXT.danger)}>
            One kind at more than half the volume buries everything a dispatcher could act on.
            Check whether it re-notifies on a timer instead of on a change.
          </p>
        </div>
      )}

      <section>
        <h2 className={reportSectionTitle}>Alert health</h2>
        <div className="mt-4">
          <KpiStrip>
            <KpiCard label="Total" value={total} />
            <KpiCard label="Critical" value={critical} tone="danger" dot />
            <KpiCard label="Distinct kinds" value={profile.length} sub="spread is health" />
            <KpiCard
              label="Acknowledged"
              value={total > 0 ? Math.round((acked / total) * 100) : 0}
              unit="%"
              sub={acked === 0 ? 'nobody is working the queue' : 'of alerts actioned'}
            />
          </KpiStrip>
        </div>
      </section>

      <section className={`${reportCard} overflow-hidden`}>
        <div className="flex flex-wrap items-start justify-between gap-2 border-b border-slate-200 px-6 py-4">
          <div>
            <h3 className={reportCardTitle}>What is firing</h3>
            <p className={`mt-0.5 ${reportCardSub}`}>
              A kind spread across many vehicles is systemic. Concentrated on one is that vehicle.
            </p>
          </div>
          <label className="flex items-center gap-1.5 text-[11px] font-medium text-slate-600">
            <input type="checkbox" checked={actionableOnly} onChange={(e) => setActionableOnly(e.target.checked)} />
            actionable only
          </label>
        </div>
        <div className="p-4 pt-0">
          <ReportTable
            columns={profileColumns}
            rows={visibleProfile}
            columnDefs={profileDefs}
            onRowClick={setFilters ? (row) => togglePick('kind', String(row.kind)) : undefined}
            compact
            className="ring-0 shadow-none"
            emptyTitle="No actionable kinds in this window"
            emptyBody="Untick “actionable only” to see every kind that fired."
          />
        </div>
      </section>

      <section>
        <div className="flex items-baseline justify-between">
          <h2 className={reportSectionTitle}>Who repeats</h2>
          <button
            type="button"
            onClick={() => void downloadXlsx(offenders, offenderColumns, 'alert-repeat-offenders', 'Repeat offenders', offenderLabels)}
            className={btnGhost}
          >
            export Excel
          </button>
        </div>
        <p className={reportSectionSub}>
          Per 100 km, not per driver. Someone who ran twice as far will have twice as many events.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {offenders.slice(0, 6).map((r, i) => {
            const rate = num(r.events_per_100km) ?? 0
            return (
              <div
                key={i}
                role={setFilters ? 'button' : undefined}
                tabIndex={setFilters ? 0 : undefined}
                aria-pressed={setFilters ? (pick?.by === 'driver' && pick.value === String(r.driver_name)) : undefined}
                onClick={setFilters ? () => togglePick('driver', String(r.driver_name)) : undefined}
                onKeyDown={setFilters ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePick('driver', String(r.driver_name)) } } : undefined}
                className={clsx(
                  reportCard, reportCardPadSm,
                  setFilters && 'cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]',
                  pick?.by === 'driver' && pick.value === String(r.driver_name) && 'ring-2 ring-[#2563EB]',
                )}
              >
                <div className="flex items-baseline justify-between">
                  <div className="text-sm font-medium text-slate-900">{String(r.driver_name)}</div>
                  <div className={reportCardSub}>{String(r.km)} km · {String(r.legs)} legs</div>
                </div>
                <div className={`mt-0.5 font-mono ${reportCardSub}`}>
                  {String(r.vehicle_id)}
                  {(num(r.vehicles_driven) ?? 1) > 1 ? ` +${(num(r.vehicles_driven) ?? 1) - 1} more` : ''}
                </div>
                <div className="mt-4 flex items-end gap-2">
                  <div className={clsx('text-2xl font-semibold tabular-nums', rate > worstRate * 0.66 ? TONE_TEXT.danger : 'text-slate-900')}>
                    {rate}
                  </div>
                  <div className={`pb-1 ${reportCardSub}`}>events / 100 km</div>
                </div>
                <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100">
                  <div className="h-1.5 rounded-full bg-red-500" style={{ width: `${worstRate > 0 ? Math.max(2, (rate / worstRate) * 100) : 0}%` }} />
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <div className="text-slate-500">overspeed</div>
                    <div className="font-mono tabular-nums text-slate-700">{String(r.overspeed_per_100km ?? '—')}</div>
                  </div>
                  <div>
                    <div className="text-slate-500">harsh brake</div>
                    <div className="font-mono tabular-nums text-slate-700">{String(r.harsh_brake_per_100km ?? '—')}</div>
                  </div>
                  <div>
                    <div className="text-slate-500">escalations</div>
                    <div className={clsx('font-mono tabular-nums', (num(r.escalations) ?? 0) > 0 ? clsx('font-medium', TONE_TEXT.danger) : 'text-slate-700')}>
                      {String(r.escalations)}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </section>

      {setFilters ? (
        <AlertDrillList
          filters={filters}
          setFilters={setFilters}
          scope={scope}
          idleHint="Click an alert type in the table or a driver card above to see just those alerts."
        />
      ) : null}
    </div>
  )
}
